import type { EmbeddedStudioDefaultProps } from "@mountayaapp/types/api/api";

import { type EmbedConfig, embedSearchSchema, resolveEmbedConfig } from "./searchParams";

/**
 * EmbedConfigFieldKey is the subset of EmbedConfig keys the embed wizard exposes
 * as configuration — the keys EMBED_CONFIG_FIELDS pairs with a URL param. It
 * deliberately excludes the content selectors (the itinerary/collection/zone ids,
 * the activity, the overlay, the time offset) and the non-configurable `embedded`
 * flag. So a descriptor can only ever name a field the snippet builder is allowed
 * to override.
 */
export type EmbedConfigFieldKey =
  | "preferredLanguage"
  | "preferredTheme"
  | "preferredUnitLength"
  | "preferredUnitSlope"
  | "preferredUnitTemperature"
  | "segmentsColor"
  | "terrain"
  | "terrainVariant"
  | "withDirectionsControl"
  | "withDownloadControl"
  | "withNavigationControl"
  | "withOverlays"
  | "withOverlaysControl"
  | "withPreferencesControl"
  | "withProfileAnalysis"
  | "withProfileCharacteristics"
  | "withProfileElevation"
  | "withStreetviewControl"
  | "withTerrains";

/**
 * EmbedConfigFieldKind tags how a descriptor's value serializes into the URL: a
 * `boolean` flag becomes `"true"`/`"false"`, an `enum` or `string` becomes its
 * own text, and a `stringArray` emits one repeated param per element. The kind
 * also tells the diff how to compare a value against the org default (a scalar
 * compare for the first three, an order-insensitive contents compare for the
 * array).
 */
export type EmbedConfigFieldKind = "boolean" | "enum" | "string" | "stringArray";

/**
 * EmbedConfigField pairs one configurable EmbedConfig field with the snake_case
 * search-param name the embed route reads and the kind that drives both its
 * serialization and its default-diff. The `key` is constrained to
 * EmbedConfigFieldKey, so a descriptor cannot reference a content selector or a
 * non-EmbedConfig field. The list of these (EMBED_CONFIG_FIELDS) is the shared
 * source of truth the URL serializer and the wizard's form controls both drive
 * from.
 */
export interface EmbedConfigField {
  /** key is the EmbedConfig field this descriptor configures. */
  key: EmbedConfigFieldKey;
  /** kind tags how the value serializes and how it is diffed against the default. */
  kind: EmbedConfigFieldKind;
  /** param is the snake_case search-param name the embed route reads for this field. */
  param: string;
}

/**
 * EMBED_CONFIG_FIELDS is the ordered descriptor list pairing each configurable
 * EmbedConfig field with its embed search-param name and kind. It is the shared
 * vocabulary the snippet serializer (buildEmbedSearch) and the wizard form
 * controls both drive from, so the params a snippet can override and the controls
 * the form renders never drift. The order is grouped by configuration section
 * (Appearance, Map controls, Profile, Layers) and, within each section,
 * follows the order the controls render on screen — the top-right control stack for
 * Map controls, the elevation panel top-to-bottom for Profile — so a section's rail
 * order, the serialized search, and the rendered surface all read the same. The
 * content selectors and the always-on `embedded` flag are intentionally absent: a
 * snippet authors the display config, not the content selection (that comes from the
 * itinerary the wizard opened for) and not the always-on flag.
 */
export const EMBED_CONFIG_FIELDS: readonly EmbedConfigField[] = [
  // Appearance.
  { key: "preferredLanguage", kind: "enum", param: "preferred_language" },
  { key: "preferredTheme", kind: "enum", param: "preferred_theme" },
  { key: "preferredUnitLength", kind: "enum", param: "preferred_unit_length" },
  { key: "preferredUnitSlope", kind: "enum", param: "preferred_unit_slope" },
  { key: "preferredUnitTemperature", kind: "enum", param: "preferred_unit_temperature" },
  { key: "terrain", kind: "string", param: "terrain" },
  { key: "terrainVariant", kind: "enum", param: "terrain_variant" },
  { key: "segmentsColor", kind: "string", param: "segments_color" },
  // Map controls, in the top-right stack's render order: zoom, preferences, download,
  // directions, Street View (the layers button moved to the Layers section below).
  { key: "withNavigationControl", kind: "boolean", param: "with_navigation_control" },
  { key: "withPreferencesControl", kind: "boolean", param: "with_preferences_control" },
  { key: "withDownloadControl", kind: "boolean", param: "with_download_control" },
  { key: "withDirectionsControl", kind: "boolean", param: "with_directions_control" },
  { key: "withStreetviewControl", kind: "boolean", param: "with_streetview_control" },
  // Profile panel, in the elevation panel's render order: characteristics, elevation,
  // analysis.
  { key: "withProfileCharacteristics", kind: "boolean", param: "with_profile_characteristics" },
  { key: "withProfileElevation", kind: "boolean", param: "with_profile_elevation" },
  { key: "withProfileAnalysis", kind: "boolean", param: "with_profile_analysis" },
  // Layers: the layers-button toggle that governs the panel, then the terrain and
  // overlay allowlists it offers.
  { key: "withOverlaysControl", kind: "boolean", param: "with_overlays_control" },
  { key: "withTerrains", kind: "stringArray", param: "with_terrains" },
  { key: "withOverlays", kind: "stringArray", param: "with_overlays" },
] as const;

/**
 * EMPTY_EMBED_SEARCH is the parsed empty search every embed field falls through,
 * so resolveEmbedDefaults resolves each field to the org default rather than a URL
 * override. It is the merged embed schema applied to an empty object — every field
 * is optional there, so the parse yields all-undefined and the resolver takes the
 * default_props branch for every field. Parsed once and frozen because it is a
 * constant input, never mutated.
 */
const EMPTY_EMBED_SEARCH = embedSearchSchema.parse({});

/**
 * resolveEmbedDefaults resolves the org's default_props into the fully-resolved
 * EmbedConfig the wizard form initializes to and the Reset action returns to. It
 * folds an empty search over the default_props, so every field falls to the org
 * default (or the hardcoded fallback when the org leaves it unset) — the same
 * precedence the live embed route applies, minus any URL override. It is the
 * baseline buildEmbedSearch diffs against: a configured value equal to this
 * default emits no param.
 */
export const resolveEmbedDefaults = (defaultProps: EmbeddedStudioDefaultProps): EmbedConfig =>
  resolveEmbedConfig(EMPTY_EMBED_SEARCH, defaultProps);

/**
 * arraysDifferByContents reports whether two string arrays differ by their
 * contents, ignoring order — the diff a stringArray descriptor uses so a
 * reordered-but-equal overlay/terrain list is not treated as an override. Compares
 * by length and a sorted element-wise walk, so `["a","b"]` and `["b","a"]` are
 * equal while `["a"]` and `["a","b"]` differ.
 */
const arraysDifferByContents = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) {
    return true;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  return sortedA.some((value, index) => value !== sortedB[index]);
};

/**
 * fieldEntries emits the ordered [param, value] pairs a single descriptor
 * contributes when its configured value differs from the resolved default, or an
 * empty array when it matches (a value equal to the default is never emitted — a
 * param exists solely to override the org default). A boolean serializes to
 * "true"/"false"; an enum/string serializes to its own text; a stringArray emits
 * one pair per element (a repeated key) and only when its contents differ from the
 * default array.
 */
const fieldEntries = (
  field: EmbedConfigField,
  config: EmbedConfig,
  defaults: EmbedConfig,
): Array<[string, string]> => {
  if (field.kind === "stringArray") {
    const value = config[field.key] as readonly string[];
    const fallback = defaults[field.key] as readonly string[];
    if (!arraysDifferByContents(value, fallback)) {
      return [];
    }

    return value.map((element) => [field.param, element]);
  }

  if (field.kind === "boolean") {
    const value = config[field.key] as boolean;
    if (value === (defaults[field.key] as boolean)) {
      return [];
    }

    return [[field.param, value ? "true" : "false"]];
  }

  // enum / string: a plain string value, emitted only when it diverges from the
  // resolved default.
  const value = config[field.key] as string;
  if (value === (defaults[field.key] as string)) {
    return [];
  }

  return [[field.param, value]];
};

/**
 * BuildEmbedSearchInput is the input to buildEmbedSearch: the publishable key the
 * snippet always carries, the content selection (a collection, an itinerary, or
 * both), the optional session token (present only for require_session_token orgs),
 * the live config the wizard authored, and the resolved org defaults to diff
 * against. At least one of collectionId / itineraryId names what the embed renders;
 * a caller with neither builds a snippet with no content, which the embed route
 * answers with its own empty state.
 */
export interface BuildEmbedSearchInput {
  /**
   * collectionId is the collection the embed renders. Alone it selects the whole
   * collection; alongside an itinerary it frames the collection on that one.
   * Omitted from the search when absent.
   */
  collectionId?: string;
  /** config is the live embed configuration the wizard authored. */
  config: EmbedConfig;
  /** defaults is the resolved org defaults the config is diffed against. */
  defaults: EmbedConfig;
  /**
   * itineraryId is the itinerary the embed renders, alone or as the frame inside a
   * collection. Omitted from the search when the embed targets a whole collection.
   */
  itineraryId?: string;
  /** publishableKey resolves the org for the embed auth strategy. */
  publishableKey: string;
  /**
   * sessionToken is the optional embed session token for a require_session_token
   * org; omitted from the search when absent.
   */
  sessionToken?: string;
}

/**
 * buildEmbedSearch builds the ordered embed search-param pairs the snippet's iframe
 * src carries. The order is deterministic for testability: the publishable key
 * first, then the itinerary id when present, then the collection id when present,
 * then the session token when present, then — for each descriptor in EMBED_CONFIG_FIELDS, in
 * list order — a param ONLY when the configured value differs from the resolved
 * default. A value equal to the org default is never emitted, so the snippet stays
 * minimal and a publisher's URL carries only the overrides they actually changed.
 * Booleans serialize to "true"/"false"; arrays emit one repeated entry per element
 * and differ by contents (order-insensitive). Returns pairs (not a string) so the
 * caller renders them through URLSearchParams.
 */
export const buildEmbedSearch = (input: BuildEmbedSearchInput): Array<[string, string]> => {
  const entries: Array<[string, string]> = [["publishable_key", input.publishableKey]];

  // Either content selector may stand alone. An itinerary alone embeds that route; a
  // collection alone embeds the whole folder; together they embed the collection
  // framed on the one itinerary.
  if (input.itineraryId) {
    entries.push(["itinerary_id", input.itineraryId]);
  }

  if (input.collectionId) {
    entries.push(["collection_id", input.collectionId]);
  }

  if (input.sessionToken) {
    entries.push(["session_token", input.sessionToken]);
  }

  for (const field of EMBED_CONFIG_FIELDS) {
    entries.push(...fieldEntries(field, input.config, input.defaults));
  }

  return entries;
};

/**
 * buildEmbedSrc renders the ordered search pairs into the embed iframe's src — the
 * `${origin}/studio/embed` route with the params serialized through
 * URLSearchParams (which percent-encodes each value), plus an optional URL `hash`
 * fragment appended verbatim (the `#view=…` anchor MapLibre reads — see
 * buildEmbedViewHash). The origin is passed in (the in-app preview uses
 * window.location.origin, the copyable snippet uses the public app origin), so this
 * stays free of env and DOM and remains a pure testable leaf.
 */
export const buildEmbedSrc = (
  origin: string,
  search: Array<[string, string]>,
  hash = "",
): string => {
  const params = new URLSearchParams(search);

  return `${origin}/studio/embed?${params.toString()}${hash}`;
};

/**
 * EmbedViewAnchor is the captured per-embed default camera buildEmbedViewHash encodes
 * (each channel null when the publisher left it unset). It mirrors the wizard's
 * DefaultView shape structurally so core stays free of the app's type.
 */
export interface EmbedViewAnchor {
  bearing: number | null;
  center: [number, number] | null;
  pitch: number | null;
  zoom: number | null;
}

/**
 * buildEmbedViewHash encodes the captured default view as MapLibre's `#view` URL hash —
 * `#view=<zoom>/<lat>/<lng>/<bearing>/<pitch>`, the exact anchor a `hash: "view"` map
 * reads on load to open framed there. The publisher captures it from the live preview,
 * and it rides the copyable snippet as this hash (NOT search params), so the embedded
 * map frames itself purely off the URL fragment. center + zoom are the essential anchor;
 * with either unset there is nothing to frame, so the hash is empty (the embed opens at
 * its plain fit-to-route). A null bearing/pitch encodes as 0 (north-up / flat). Pure
 * string output — no DOM, no env — so it stays a testable leaf.
 */
export const buildEmbedViewHash = (view: EmbedViewAnchor): string => {
  if (view.center === null || view.zoom === null) {
    return "";
  }

  const [lng, lat] = view.center;
  const bearing = Math.round(view.bearing ?? 0);
  const pitch = Math.round(view.pitch ?? 0);

  return `#view=${view.zoom.toFixed(2)}/${lat.toFixed(6)}/${lng.toFixed(6)}/${bearing}/${pitch}`;
};

/**
 * EMBED_IFRAME_RADIUS_PX is the corner radius, in px, shared by the copy-paste snippet
 * (buildEmbedIframe) and the wizard's in-app preview box, so the preview frames the embed
 * exactly as the published iframe renders it. It is a concrete px length, NOT a Mantine
 * radius token: the snippet runs on the publisher's own page where no Mantine CSS variables
 * resolve, so the value must be self-contained. 8px is the studio's `md` radius, so the
 * embed reads as a finished card on the host page.
 */
export const EMBED_IFRAME_RADIUS_PX = 8;

/**
 * BuildEmbedIframeInput is the input to buildEmbedIframe: the iframe src and the
 * width/height the publisher chose for the embed.
 */
export interface BuildEmbedIframeInput {
  /** height is the iframe height (a CSS length, e.g. "480" or "480px"). */
  height: string;
  /** src is the embed iframe src built by buildEmbedSrc. */
  src: string;
  /** title is the iframe's accessible title. */
  title: string;
  /** width is the iframe width (a CSS length, e.g. "100%" or "640"). */
  width: string;
}

/**
 * escapeHtmlAttribute rewrites a string for safe interpolation into a double-quoted HTML
 * attribute value. The ampersand is replaced first so the entities the later passes
 * introduce are not themselves re-escaped, then the angle brackets and the double quote.
 * buildEmbedIframe runs the iframe title (the itinerary name) through it: the name is
 * publisher-controlled free text, so an unescaped `"`, `&`, `<`, or `>` would break out of
 * the title attribute and malform the copy-paste snippet. The apostrophe is deliberately
 * left as-is, since a double-quoted attribute does not require escaping it, so a common name
 * like `Tour de l'Île` stays readable. The other attributes need no escaping: the src is
 * already percent-encoded by URLSearchParams and the width/height are fixed constants.
 */
const escapeHtmlAttribute = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * buildEmbedIframe renders the copy-paste `<iframe>` HTML a publisher pastes into
 * their site: the src, the chosen width/height, a zero border with rounded corners
 * (8px, the studio's `md` radius, so the embed reads as a finished card on the
 * host page), lazy loading, and an accessible, HTML-escaped title. It carries no
 * `allow="fullscreen"`: the embedded studio offers no fullscreen affordance, so the
 * permission would be dead. It is indented for readability because it is copy-paste
 * output a human reads. Pure string output (no DOM, no env), so it stays a testable
 * leaf.
 */
export const buildEmbedIframe = (input: BuildEmbedIframeInput): string =>
  [
    "<iframe",
    `  src="${input.src}"`,
    `  width="${input.width}"`,
    `  height="${input.height}"`,
    `  title="${escapeHtmlAttribute(input.title)}"`,
    `  style="border:0;border-radius:${EMBED_IFRAME_RADIUS_PX}px"`,
    '  loading="lazy"',
    "></iframe>",
  ].join("\n");
